import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  Checkbox,
  ListItemText,
  ListSubheader,
  Grid,
  FormGroup,
  FormControlLabel
} from '@mui/material';
import { PriceCheck, Refresh, Search as SearchIcon, Download } from '@mui/icons-material';
import { API_URL } from './constants';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Extract country code from retailer name (e.g., "Snusbolaget (SE)" -> "SE")
const extractCountryFromRetailer = (retailerName) => {
  const match = retailerName.match(/\(([A-Z]{2})\)/);
  return match ? match[1] : null;
};

// Extract country name from retailer name (e.g., "Snusbolaget (SE)" -> "Sweden")
const getCountryName = (countryCode) => {
  const countryMap = {
    'SE': 'Sweden',
    'NO': 'Norway',
    'DK': 'Denmark',
    'FI': 'Finland',
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy',
    'ES': 'Spain',
    'PL': 'Poland',
    'CZ': 'Czech Republic',
    'AT': 'Austria',
    'CH': 'Switzerland',
    'BE': 'Belgium',
    'NL': 'Netherlands',
    'GB': 'United Kingdom',
    'IE': 'Ireland',
  };
  return countryMap[countryCode] || countryCode;
};

const ExportPricingDataView = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toBrandsData, setToBrandsData] = useState(null);
  const [moBrandsData, setMoBrandsData] = useState(null);
  const [selectedDate, setSelectedDate] = useState('2026-02-05');
  const [selectedCategories, setSelectedCategories] = useState(['TO', 'MO']);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  useEffect(() => {
    fetchPricingData();
  }, []);

  // Combine data based on selected categories
  const combinedData = useMemo(() => {
    const dataSources = [];
    if (selectedCategories.includes('TO') && toBrandsData) dataSources.push(toBrandsData);
    if (selectedCategories.includes('MO') && moBrandsData) dataSources.push(moBrandsData);
    
    if (dataSources.length === 0) return null;
    if (dataSources.length === 1) return dataSources[0];
    
    // Combine multiple data sources
    const allColumns = new Set();
    const allRows = [];
    
    dataSources.forEach(source => {
      if (source.columns) {
        source.columns.forEach(col => allColumns.add(col));
      }
      if (source.data) {
        allRows.push(...source.data);
      }
    });
    
    return {
      columns: Array.from(allColumns),
      data: allRows,
      count: allRows.length
    };
  }, [toBrandsData, moBrandsData, selectedCategories]);

  // Extract available retailers, countries, and brands from ALL data (not just selected categories)
  // This ensures filters remain visible even when no categories are selected
  const { availableRetailers, availableCountries, availableBrands } = useMemo(() => {
    // Combine all available data sources for filter options
    const allDataSources = [];
    if (toBrandsData) allDataSources.push(toBrandsData);
    if (moBrandsData) allDataSources.push(moBrandsData);
    
    if (allDataSources.length === 0) {
      return { availableRetailers: [], availableCountries: [], availableBrands: [] };
    }
    
    // Combine columns from all data sources
    const allColumns = new Set();
    const allRows = [];
    
    allDataSources.forEach(source => {
      if (source.columns) {
        source.columns.forEach(col => allColumns.add(col));
      }
      if (source.data) {
        allRows.push(...source.data);
      }
    });
    
    // Get retailer columns (exclude Title, Brand, referenceEan)
    const excludeColumns = ['Title', 'Brand', 'referenceEan'];
    const retailers = Array.from(allColumns).filter(col => !excludeColumns.includes(col));
    
    // Extract unique countries from retailer names
    const countrySet = new Set();
    retailers.forEach(retailer => {
      const countryCode = extractCountryFromRetailer(retailer);
      if (countryCode) {
        countrySet.add(countryCode);
      }
    });
    
    const countries = Array.from(countrySet).sort();
    
    // Extract unique brands from all data
    const brandSet = new Set();
    allRows.forEach(row => {
      if (row.Brand) {
        brandSet.add(row.Brand);
      }
    });
    const brands = Array.from(brandSet).sort();
    
    return {
      availableRetailers: retailers,
      availableCountries: countries,
      availableBrands: brands
    };
  }, [toBrandsData, moBrandsData]);

  // Track if filters have been initialized
  const filtersInitializedRef = useRef(false);

  // Initialize selected filters when data loads
  useEffect(() => {
    if (availableRetailers.length > 0 && availableCountries.length > 0 && !filtersInitializedRef.current) {
      // Initialize categories - default to both TO and MO
      setSelectedCategories(['TO', 'MO']);
      
      // Initialize countries - default to Sweden (SE)
      const swedenCode = 'SE';
      if (availableCountries.includes(swedenCode)) {
        setSelectedCountries([swedenCode]);
      }
      
      // Initialize retailers - default to specific retailers
      const defaultRetailerNames = ['Snussidan', 'Snuset', 'Min Prilla', 'Snusbolaget'];
      const foundRetailers = availableRetailers.filter(retailer => 
        defaultRetailerNames.some(name => retailer.includes(name))
      );
      
      if (foundRetailers.length > 0) {
        setSelectedRetailers(foundRetailers);
      }
      
      // Initialize brands - select all by default
      if (availableBrands.length > 0) {
        setSelectedBrands([...availableBrands]);
      }
      
      filtersInitializedRef.current = true;
    }
  }, [availableRetailers, availableCountries, availableBrands]);

  const fetchPricingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/bat/pricing-data`, {
        params: {
          date: selectedDate
        },
        withCredentials: true
      });

      if (response.data.success) {
        setToBrandsData(response.data.to_brands);
        setMoBrandsData(response.data.mo_brands);
      } else {
        setError('Failed to fetch pricing data');
      }
    } catch (err) {
      console.error('Error fetching pricing data:', err);
      setError(err.response?.data?.detail || 'Failed to fetch pricing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleRefresh = () => {
    fetchPricingData();
  };


  const handleCountryChange = (countries) => {
    setSelectedCountries(countries);
  };

  const handleRetailerChange = (retailers) => {
    setSelectedRetailers(retailers);
  };

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories);
  };

  const handleBrandChange = (brands) => {
    setSelectedBrands(brands);
  };

  const handleExportExcel = () => {
    const dataToExport = filteredDataByBrands || combinedData;
    if (!dataToExport || !dataToExport.data || dataToExport.data.length === 0) {
      return;
    }

    const allColumns = dataToExport.columns || [];
    let allRows = dataToExport.data || [];

    // Sort rows by Brand
    if (allRows.length > 0 && allColumns.includes('Brand')) {
      allRows = [...allRows].sort((a, b) => {
        const brandA = (a.Brand || '').toString().toLowerCase();
        const brandB = (b.Brand || '').toString().toLowerCase();
        return brandA.localeCompare(brandB);
      });
    }

    // Reorder columns: Brand first, then Title, then selected retailers
    const baseColumns = ['Brand', 'Title'];
    const retailerColumns = visibleRetailers.filter(col => allColumns.includes(col));
    const orderedColumns = [
      ...baseColumns.filter(col => allColumns.includes(col)),
      ...retailerColumns
    ];

    // Prepare data for Excel export
    const excelData = [
      orderedColumns, // Header row
      ...allRows.map(row => orderedColumns.map(col => row[col] ?? ''))
    ];

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pricing Data');

    // Set column widths
    const colWidths = orderedColumns.map(col => {
      if (col === 'Brand') return { wch: 15 };
      if (col === 'Title') return { wch: 40 };
      return { wch: 15 };
    });
    ws['!cols'] = colWidths;

    // Format date: YYYYMMDD
    const dateStr = selectedDate.replace(/-/g, '');
    const filename = `${dateStr}_pricing_data.xlsx`;

    // Export file
    XLSX.writeFile(wb, filename);
  };

  // Filter retailers by selected countries
  const filteredRetailersByCountry = useMemo(() => {
    if (selectedCountries.length === 0) return availableRetailers;
    
    return availableRetailers.filter(retailer => {
      const countryCode = extractCountryFromRetailer(retailer);
      return countryCode && selectedCountries.includes(countryCode);
    });
  }, [availableRetailers, selectedCountries]);

  // Get retailers that match the selected countries and are in selectedRetailers
  const visibleRetailers = useMemo(() => {
    return filteredRetailersByCountry.filter(retailer => 
      selectedRetailers.includes(retailer)
    );
  }, [filteredRetailersByCountry, selectedRetailers]);

  // Filter rows by selected brands
  const filteredDataByBrands = useMemo(() => {
    if (!combinedData || !combinedData.data) return combinedData;
    if (selectedBrands.length === 0) return combinedData;
    
    const filteredRows = combinedData.data.filter(row => 
      row.Brand && selectedBrands.includes(row.Brand)
    );
    
    return {
      ...combinedData,
      data: filteredRows,
      count: filteredRows.length
    };
  }, [combinedData, selectedBrands]);

  const categoryOptions = [
    { value: 'TO', label: 'Traditional Oral (TO Brands)' },
    { value: 'MO', label: 'Modern Oral (MO Brands)' }
  ];

  const renderFilters = () => {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Category Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-multiple-select-label">Categories</InputLabel>
              <Select
                labelId="category-multiple-select-label"
                id="category-multiple-select"
                value={selectedCategories}
                label="Categories"
                multiple
                onChange={(e) => setSelectedCategories(e.target.value)}
                input={<OutlinedInput label="Categories" />}
                renderValue={(selected) => selected.map(cat => {
                  const option = categoryOptions.find(opt => opt.value === cat);
                  return option ? option.label : cat;
                }).join(', ')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '400px',
                      width: '280px',
                    },
                  },
                  autoFocus: false,
                }}
                onClose={() => setCategorySearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search categories..."
                    fullWidth
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectedCategories.length === categoryOptions.length) {
                        setSelectedCategories([]);
                      } else {
                        setSelectedCategories(categoryOptions.map(opt => opt.value));
                      }
                    }}
                  >
                    {selectedCategories.length === categoryOptions.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                {categoryOptions
                  .filter((option) =>
                    option.label.toLowerCase().includes(categorySearchTerm.toLowerCase())
                  )
                  .map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      dense
                      sx={{
                        py: 0.25,
                        minHeight: 'auto',
                      }}
                    >
                      <Checkbox checked={selectedCategories.includes(option.value)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={option.label} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Brand Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="brand-multiple-select-label">Brands</InputLabel>
              <Select
                labelId="brand-multiple-select-label"
                id="brand-multiple-select"
                value={selectedBrands}
                label="Brands"
                multiple
                onChange={(e) => setSelectedBrands(e.target.value)}
                input={<OutlinedInput label="Brands" />}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '400px',
                      width: '280px',
                    },
                  },
                  autoFocus: false,
                }}
                onClose={() => setBrandSearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search brands..."
                    fullWidth
                    value={brandSearchTerm}
                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectedBrands.length === availableBrands.length) {
                        setSelectedBrands([]);
                      } else {
                        setSelectedBrands([...availableBrands]);
                      }
                    }}
                  >
                    {selectedBrands.length === availableBrands.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                {availableBrands
                  .filter((brand) =>
                    brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
                  )
                  .map((brand) => (
                    <MenuItem
                      key={brand}
                      value={brand}
                      dense
                      sx={{
                        py: 0.25,
                        minHeight: 'auto',
                      }}
                    >
                      <Checkbox checked={selectedBrands.includes(brand)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={brand} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Country Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="country-multiple-select-label">Countries</InputLabel>
              <Select
                labelId="country-multiple-select-label"
                id="country-multiple-select"
                value={selectedCountries}
                label="Countries"
                multiple
                onChange={(e) => handleCountryChange(e.target.value)}
                input={<OutlinedInput label="Countries" />}
                renderValue={(selected) => selected.map(code => getCountryName(code)).join(', ')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '400px',
                      width: '280px',
                    },
                  },
                  autoFocus: false,
                }}
                onClose={() => setCountrySearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search countries..."
                    fullWidth
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectedCountries.length === availableCountries.length) {
                        handleCountryChange([]);
                      } else {
                        handleCountryChange([...availableCountries]);
                      }
                    }}
                  >
                    {selectedCountries.length === availableCountries.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                {availableCountries
                  .filter((country) =>
                    getCountryName(country).toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
                    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
                  )
                  .map((country) => (
                    <MenuItem
                      key={country}
                      value={country}
                      dense
                      sx={{
                        py: 0.25,
                        minHeight: 'auto',
                      }}
                    >
                      <Checkbox checked={selectedCountries.includes(country)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={`${getCountryName(country)} (${country})`} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Retailer Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="retailer-multiple-select-label">Retailers</InputLabel>
              <Select
                labelId="retailer-multiple-select-label"
                id="retailer-multiple-select"
                value={selectedRetailers}
                label="Retailers"
                multiple
                onChange={(e) => handleRetailerChange(e.target.value)}
                input={<OutlinedInput label="Retailers" />}
                renderValue={(selected) => {
                  const displayRetailers = selected.filter(r => filteredRetailersByCountry.includes(r));
                  return displayRetailers.length > 0 
                    ? displayRetailers.join(', ')
                    : 'No retailers selected';
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '400px',
                      width: '280px',
                    },
                  },
                  autoFocus: false,
                }}
                onClose={() => setRetailerSearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search retailers..."
                    fullWidth
                    value={retailerSearchTerm}
                    onChange={(e) => setRetailerSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const filteredRetailers = filteredRetailersByCountry;
                      if (selectedRetailers.filter(r => filteredRetailers.includes(r)).length === filteredRetailers.length) {
                        // Deselect all filtered retailers
                        handleRetailerChange(selectedRetailers.filter(r => !filteredRetailers.includes(r)));
                      } else {
                        // Select all filtered retailers
                        const newSelection = [...new Set([...selectedRetailers, ...filteredRetailers])];
                        handleRetailerChange(newSelection);
                      }
                    }}
                  >
                    {selectedRetailers.filter(r => filteredRetailersByCountry.includes(r)).length === filteredRetailersByCountry.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </Box>
                {filteredRetailersByCountry
                  .filter((retailer) =>
                    retailer.toLowerCase().includes(retailerSearchTerm.toLowerCase())
                  )
                  .map((retailer) => (
                    <MenuItem
                      key={retailer}
                      value={retailer}
                      dense
                      sx={{
                        py: 0.25,
                        minHeight: 'auto',
                      }}
                    >
                      <Checkbox checked={selectedRetailers.includes(retailer)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={retailer} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
    );
  };

  const renderTable = (data) => {
    // Use filtered data by brands
    const displayData = filteredDataByBrands || data;
    
    if (!displayData || !displayData.data || displayData.data.length === 0) {
      return (
        <Alert severity="info">
          No data available for the selected filters.
        </Alert>
      );
    }

    const allColumns = displayData.columns || [];
    let allRows = displayData.data || [];

    // Sort rows by Brand
    if (allRows.length > 0 && allColumns.includes('Brand')) {
      allRows = [...allRows].sort((a, b) => {
        const brandA = (a.Brand || '').toString().toLowerCase();
        const brandB = (b.Brand || '').toString().toLowerCase();
        return brandA.localeCompare(brandB);
      });
    }

    // Reorder columns: Brand first, then Title, then selected retailers
    const baseColumns = ['Brand', 'Title'];
    const retailerColumns = visibleRetailers.filter(col => allColumns.includes(col));
    const orderedColumns = [
      ...baseColumns.filter(col => allColumns.includes(col)),
      ...retailerColumns
    ];

    return (
      <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {orderedColumns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: column === 'Brand' ? '20px' : undefined,
                      minWidth: column === 'Brand' ? '20px' : undefined,
                      maxWidth: column === 'Brand' ? '20px' : undefined
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allRows.map((row, index) => (
                <TableRow key={index} hover>
                  {orderedColumns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        width: column === 'Brand' ? '20px' : undefined,
                        minWidth: column === 'Brand' ? '20px' : undefined,
                        maxWidth: column === 'Brand' ? '20px' : column === 'Title' ? 300 : 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: column === 'Title' ? 'normal' : 'nowrap'
                      }}
                      title={row[column]?.toString() || ''}
                    >
                      {row[column] !== null && row[column] !== undefined
                        ? row[column].toString()
                        : '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Export pricing data
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Date"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{ width: 200 }}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportExcel}
            disabled={loading || !combinedData || !combinedData.data || combinedData.data.length === 0}
            color="primary"
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and export pricing data for various brands and products.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            {/* Filters - always visible */}
            {renderFilters()}
            
            {/* Display combined table */}
            {renderTable(combinedData)}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ExportPricingDataView;
